import type { TaskDetail, WorkflowInstance } from '@pm/schemas';
import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogFooter,
  Label,
  LabelCaps,
  Select,
  Skeleton,
  Textarea,
} from '@pm/ui';
import { Check, ChevronRight, Lock, ShieldAlert, Workflow } from 'lucide-react';
import { useState } from 'react';
import { api, ApiRequestError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useTaskMutation, useTaskWorkflow, useWorkflowTemplates, wfKeys } from '@/lib/queries';
import { useToast } from '@/lib/toast';
import { useQueryClient } from '@tanstack/react-query';

/** Códigos em que a API aceita a movimentação mediante override com justificativa (RULE-018). */
const OVERRIDE_CODES = new Set([
  'WORKFLOW_STAGE_LOCKED',
  'WORKFLOW_OVERRIDE_JUSTIFICATION_REQUIRED',
]);

/** Painel de workflow na tela da tarefa: progresso das etapas, mover, override com justificativa. */
export function WorkflowPanel({ task }: { task: TaskDetail }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { can } = useAuth();
  const isMother = !task.parent_id;
  const { data: wf, isLoading } = useTaskWorkflow(task.id, Boolean(task.workflow_instance_id));
  const { data: templates = [] } = useWorkflowTemplates();
  const [override, setOverride] = useState<{ toSort: number; name: string } | null>(null);
  const [justification, setJustification] = useState('');
  const [attachId, setAttachId] = useState('');

  const invalidateWf = () => void qc.invalidateQueries({ queryKey: wfKeys.taskWorkflow(task.id) });
  const move = useTaskMutation(
    ({ toSort, justification: j }: { toSort: number; justification?: string }) =>
      api
        .post(`/tasks/${task.id}/move-stage`, { to_sort_order: toSort, justification: j })
        .then(() => ({ id: task.id })),
    { onSuccess: invalidateWf },
  );
  const attach = useTaskMutation(
    (templateId: string) =>
      api.post(`/tasks/${task.id}/attach-workflow`, { workflow_template_id: templateId }),
    { onSuccess: invalidateWf },
  );

  function tryMove(stage: WorkflowInstance['stages'][number]) {
    move.mutate(
      { toSort: stage.sort_order },
      {
        onSuccess: () => toast.success(`Movida para ${stage.name}`),
        onError: (err) => {
          if (
            err instanceof ApiRequestError &&
            OVERRIDE_CODES.has(err.code) &&
            can('tasks.override_workflow')
          )
            setOverride({ toSort: stage.sort_order, name: stage.name });
          else toast.error(err);
        },
      },
    );
  }

  if (!task.workflow_instance_id) {
    if (!isMother || task.status === 'COMPLETED' || task.status === 'CANCELED' || !task.can.edit)
      return null;
    const published = templates.filter((t) => t.published);
    if (published.length === 0) return null;
    return (
      <div className="flex flex-col gap-1.5">
        <LabelCaps>Workflow</LabelCaps>
        <div className="flex gap-2">
          <Select
            value={attachId}
            onChange={(e) => setAttachId(e.target.value)}
            className="h-control-sm text-sm"
          >
            <option value="">Iniciar workflow…</option>
            {published.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} (v{t.published!.version})
              </option>
            ))}
          </Select>
          <Button
            variant="secondary"
            size="sm"
            disabled={!attachId}
            loading={attach.isPending}
            onClick={() =>
              attach.mutate(attachId, {
                onSuccess: () => toast.success('Workflow iniciado'),
                onError: (e) => toast.error(e),
              })
            }
          >
            <Workflow />
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading || !wf) return <Skeleton className="h-16 rounded-md" />;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <LabelCaps>Workflow</LabelCaps>
        <span className="text-sm">{wf.template.name}</span>
        <span className="font-mono text-xs text-text-3">v{wf.version}</span>
        {wf.status === 'COMPLETED' ? (
          <Badge tone="success">concluído</Badge>
        ) : wf.status === 'CANCELED' ? (
          <Badge tone="neutral">cancelado</Badge>
        ) : null}
      </div>
      <ol className="flex flex-col gap-1">
        {wf.stages.map((s) => {
          const current = s.id === wf.current_stage_id;
          const done = s.status === 'COMPLETED';
          const canMove = wf.can.move && !current && wf.status === 'ACTIVE';
          return (
            <li
              key={s.id}
              className={cn(
                'flex items-center gap-2 rounded px-2 py-1.5 text-sm',
                current && 'bg-accent-soft',
              )}
            >
              <span
                className={cn(
                  'grid size-4 place-items-center rounded-full border text-[10px] font-mono',
                  done
                    ? 'border-success bg-success text-text-inverse'
                    : current
                      ? 'border-accent text-accent'
                      : s.status === 'SKIPPED'
                        ? 'border-border-strong text-text-3 line-through'
                        : 'border-border-strong text-text-3',
                )}
                style={!done && !current && s.color ? { borderColor: s.color } : undefined}
              >
                {done ? <Check className="size-2.5" strokeWidth={3} /> : s.sort_order + 1}
              </span>
              <span
                className={cn(
                  'flex-1 truncate',
                  current && 'font-medium',
                  s.status === 'SKIPPED' && 'text-text-3 line-through',
                )}
              >
                {s.name}
              </span>
              <span className="font-mono text-xs text-text-3">
                {s.tasks_completed}/{s.tasks_total}
              </span>
              {current && s.tasks_required_open > 0 ? (
                <span className="font-mono text-[10px] text-warning">
                  {s.tasks_required_open} obrig.
                </span>
              ) : null}
              {!s.manual_entry_allowed && !current ? (
                <Lock className="size-3 text-text-3" aria-label="Sem entrada manual" />
              ) : null}
              {canMove ? (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Mover para ${s.name}`}
                  loading={move.isPending}
                  onClick={() => tryMove(s)}
                >
                  <ChevronRight />
                </Button>
              ) : null}
            </li>
          );
        })}
      </ol>

      <Dialog open={override !== null} onOpenChange={(o) => !o && setOverride(null)}>
        <DialogContent
          title="Movimentação forçada"
          description={`A etapa ${override?.name ?? ''} não permite esta movimentação. Como gestor, você pode forçar com justificativa (fica no audit log).`}
          size="sm"
        >
          <div className="flex items-center gap-2 text-sm text-warning">
            <ShieldAlert className="size-4" /> Override de workflow
          </div>
          <Label htmlFor="ov-just" className="mt-3">
            Justificativa
          </Label>
          <Textarea
            id="ov-just"
            autoFocus
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            className="mt-1.5"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOverride(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              disabled={!justification.trim()}
              loading={move.isPending}
              onClick={() =>
                override &&
                move.mutate(
                  { toSort: override.toSort, justification },
                  {
                    onSuccess: () => (
                      toast.success('Movimentação forçada registrada'),
                      setOverride(null),
                      setJustification('')
                    ),
                    onError: (e) => toast.error(e),
                  },
                )
              }
            >
              Forçar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
